import classNames from 'classnames';
import React from 'react';
import { Nullable } from '../Shared/Types';
import { errorText, info, orange, primary, red, successGlowText, warningGlowBorder, white } from '../Styles/Colours';
import styles from './AlertBanner.module.scss';
import { Button, ButtonMode, ButtonSize, ButtonVariant } from './Button';
import { AlertIcon, AlertIconRound, CloseIcon, ConfirmedIcon, InfoIcon } from './Icons';

export enum AlertBannerTypes {
    Prompt,
    Warn,
    Error,
    Success
}

interface Props {
    invertColours?: boolean;
    promptMessage?: React.ReactNode;
    promptExtraInfo?: React.ReactNode;
    infoLine?: React.ReactNode;
    promptType?: AlertBannerTypes;
    multiline?: boolean; // will truncate to one line if this is not enabled
    classNames?: string;
    iconImage?: string;
    roundIcon?: boolean;
    lowerHeight?: boolean;
    verticalSpace?: boolean;
    noBorder?: boolean;
    buttonSize?: ButtonSize;
    onDismissAlert?(value: Nullable<any>): void;
}

export function AlertBanner(props: Props) {
    const { promptType, promptMessage, infoLine, onDismissAlert, multiline, promptExtraInfo, buttonSize } = props;

    const containerClasses = classNames(styles.alertBase, props.classNames, {
        [styles.verticalSpace]: props.verticalSpace,
        [styles.invert]: props.invertColours,
        [styles.alertPrompt]: promptType === AlertBannerTypes.Prompt,
        [styles.alertWarn]: promptType === AlertBannerTypes.Warn,
        [styles.alertError]: promptType === AlertBannerTypes.Error,
        [styles.alertSuccess]: promptType === AlertBannerTypes.Success,
        [styles.multiline]: multiline || !!infoLine,
        [styles.lowerHeight]: props.lowerHeight,
        [styles.noBorder]: props.noBorder,
        [styles.moreSpecific]: props.noBorder
    });

    const iconContainer = classNames(styles.alertIconContainer, {
        [styles.multiline]: multiline,
        [styles.iconImage]: !!props.iconImage
    });

    const textContainer = classNames(styles.textContainer, {
        [styles.multiline]: multiline
    });

    let icon: JSX.Element;

    if (!!props.iconImage) {
        icon = <img src={props.iconImage} alt='' className={styles.iconImage} />;
    } else if (promptType === AlertBannerTypes.Warn) {
        icon = <AlertIcon size='20' color={props.invertColours ? white : orange} />;
    } else if (promptType === AlertBannerTypes.Error) {
        icon = props.roundIcon ? (
            <AlertIconRound size='20' color={props.invertColours ? white : red} />
        ) : (
            <AlertIcon size='20' color={props.invertColours ? white : red} />
        );
    } else if (promptType === AlertBannerTypes.Success) {
        icon = <ConfirmedIcon size='20' color={props.invertColours ? white : successGlowText} />;
    } else {
        icon = <InfoIcon size='20' color={props.invertColours ? white : info} />;
    }

    const infoLineClasses = classNames(styles.info, { [styles.noPromptMessage]: !promptMessage });

    let closeButtonVariant = ButtonVariant.info;
    let closeIconColour = primary;

    if (onDismissAlert) {
        closeIconColour =
            promptType === AlertBannerTypes.Error
                ? errorText
                : promptType === AlertBannerTypes.Warn
                ? warningGlowBorder
                : promptType === AlertBannerTypes.Success
                ? successGlowText
                : primary;

        closeButtonVariant =
            promptType === AlertBannerTypes.Error
                ? ButtonVariant.danger
                : promptType === AlertBannerTypes.Warn
                ? ButtonVariant.warning
                : promptType === AlertBannerTypes.Success
                ? ButtonVariant.success
                : ButtonVariant.info;
    }

    return (
        <div className={containerClasses}>
            <div className={iconContainer}>{icon}</div>
            <div className={textContainer}>
                {(!!promptMessage || !!promptExtraInfo) && (
                    <div className={styles.alertMessage}>
                        {promptMessage}
                        {promptExtraInfo && <span className={styles.alertExtraInfo}> {promptExtraInfo}</span>}
                    </div>
                )}
                {!!infoLine && <div className={infoLineClasses}>{infoLine}</div>}
            </div>
            {onDismissAlert && (
                <div className={styles.closeButton}>
                    <Button
                        size={buttonSize}
                        onClick={onDismissAlert}
                        iconOnly
                        mode={ButtonMode.text}
                        variant={closeButtonVariant}
                        testId='dismiss'
                        subtleShadeOnHover
                        noShadowOnHover
                    >
                        <CloseIcon color={closeIconColour} size={'16'} />
                    </Button>
                </div>
            )}
        </div>
    );
}
